import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { apiAdminCreatePdfArticle, apiAdminUploadPdf } from '../api/client';
import PortalPageShell from './PortalPageShell';

export default function AdminCreateArticle() {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [published, setPublished] = useState(true);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedSlug = useMemo(() => {
    return slug.trim() || title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }, [slug, title]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    if (!selectedFile) {
      setMessage('Please choose a PDF file to upload.');
      return;
    }

    setIsSubmitting(true);

    try {
      const uploadResponse = await apiAdminUploadPdf(selectedFile);

      if (!uploadResponse?.success || !uploadResponse?.pdf_filename) {
        setMessage(uploadResponse?.message || 'PDF upload failed.');
        return;
      }

      const createResponse = await apiAdminCreatePdfArticle({
        title,
        slug: normalizedSlug,
        excerpt,
        pdf_filename: uploadResponse.pdf_filename,
        published,
      });

      if (createResponse?.success) {
        setMessage('PDF article created successfully.');
        setTitle('');
        setSlug('');
        setExcerpt('');
        setSelectedFile(null);
        setPublished(true);
      } else {
        setMessage(createResponse?.message || 'Failed to create PDF article.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PortalPageShell title="Create Article" subtitle="Upload a PDF and publish it to the portal.">
      <Stack as="form" spacing={5} onSubmit={handleSubmit}>
        {message ? (
          <Alert status={message.includes('successfully') ? 'success' : 'error'} borderRadius="lg">
            <AlertIcon />
            {message}
          </Alert>
        ) : null}

        <FormControl isRequired>
          <FormLabel>Title</FormLabel>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Article title" />
        </FormControl>

        <FormControl>
          <FormLabel>Slug</FormLabel>
          <Input value={slug} onChange={e => setSlug(e.target.value)} placeholder="article-slug" />
          <FormHelperText>Leave blank to generate from the title.</FormHelperText>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Excerpt</FormLabel>
          <Input value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Short summary" />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>PDF File</FormLabel>
          <Input
            type="file"
            accept="application/pdf,.pdf"
            padding={1}
            onChange={e => setSelectedFile(e.target.files?.[0] || null)}
          />
          <FormHelperText>Only PDF files are supported for now.</FormHelperText>
          {selectedFile ? <Text mt={2} color="gray.600" fontSize="sm">Selected: {selectedFile.name}</Text> : null}
        </FormControl>

        <Checkbox isChecked={published} onChange={e => setPublished(e.target.checked)}>
          Publish immediately
        </Checkbox>

        <Button type="submit" colorScheme="blue" width="fit-content" isLoading={isSubmitting} loadingText="Uploading PDF">
          Upload PDF Article
        </Button>
      </Stack>
    </PortalPageShell>
  );
}
